<template>
  <div class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Logs - {{ totalItems }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">Loading logs...</div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search logs by API..."
          @input="debouncedFetchLogs"
        />
      </div>

      <!-- Date Range Filter -->
      <div class="flex items-center space-x-2">
        <input
          v-model="startDate"
          type="date"
          class="border border-gray-300 dark:border-gray-700 dark:bg-gray-600 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="fetchLogs"
        />
        <span>to</span>
        <input
          v-model="endDate"
          type="date"
          class="border border-gray-300 dark:border-gray-700 dark:bg-gray-600 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="fetchLogs"
        />
      </div>
    </div>

    <!-- Logs Table -->
    <GenericTable
      v-if="!isLoading && !error"
      :columns="columns"
      :items="logs"
      itemKey="id"
      :selectable="false"
      @rowClick="showLogDetails"
      @sort="sortLogs"
    >
      <!-- Custom cell templates -->
      <template #execution_time="{ value }"> {{ value }} s </template>
      <template #added_on="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #status_code="{ value }">
        <span :class="getStatusCodeClass(value)">{{ value }}</span>
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      v-if="!isLoading && !error"
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          @change="changeItemsPerPage"
          :options="[{ id: 10, name: '10 per page' }, { id: 20, name: '20 per page' }, { id: 50, name: '50 per page' }, { id: 100, name: '100 per page' }]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { getLogsWithPagination } from "@/services/logService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useRouter } from "vue-router";
import debounce from "lodash/debounce";

export default {
  name: "LogView",
  components: {
    SearchBar,
    GenericTable,
    GlassSelect,
  },
  setup() {
    const router = useRouter();
    const logs = ref([]);
    const searchQuery = ref("");
    const startDate = ref("");
    const endDate = ref("");
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const totalItems = ref(0);
    const totalPages = ref(1);
    const error = ref(null);
    const isLoading = ref(false);
    const sortKey = ref("added_on");
    const sortOrder = ref("desc");

    const columns = [
      { key: "api", label: "API", sortable: true, width: "30%" },
      { key: "method", label: "Method", sortable: true, width: "10%" },
      { key: "status_code", label: "Status", sortable: true, width: "10%" },
      {
        key: "execution_time",
        label: "Execution Time",
        sortable: true,
        width: "15%",
      },
      {
        key: "client_ip_address",
        label: "Client IP",
        sortable: true,
        width: "15%",
      },
      { key: "added_on", label: "Added On", sortable: true, width: "20%" },
    ];

    const isFiltered = computed(() => {
      return (
        searchQuery.value !== "" ||
        startDate.value !== "" ||
        endDate.value !== ""
      );
    });

    const fetchLogs = async () => {
      isLoading.value = true;
      try {
        error.value = null;
        const response = await getLogsWithPagination({
          page: currentPage.value,
          pageSize: itemsPerPage.value,
          search: searchQuery.value,
          startDate: startDate.value,
          endDate: endDate.value,
          ordering: `${sortOrder.value === "desc" ? "-" : ""}${sortKey.value}`,
        });
        logs.value = response.results;
        totalItems.value = response.count;
        totalPages.value = Math.ceil(response.count / itemsPerPage.value);
      } catch (err) {
        console.error("Error fetching logs:", err);
        error.value = "Failed to fetch logs. Please try again later.";
        logs.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const debouncedFetchLogs = debounce(fetchLogs, 300);

    const showLogDetails = (log) => {
      router.push({ name: "LogDetails", params: { id: log.id } });
    };

    const changePage = (delta) => {
      currentPage.value += delta;
      fetchLogs();
    };

    const changeItemsPerPage = () => {
      currentPage.value = 1;
      fetchLogs();
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const getStatusCodeClass = (statusCode) => {
      if (statusCode >= 200 && statusCode < 300) return "text-green-600";
      if (statusCode >= 400 && statusCode < 500) return "text-yellow-600";
      if (statusCode >= 500) return "text-red-600";
      return "text-gray-600";
    };

    const sortLogs = (key) => {
      if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
      } else {
        sortKey.value = key;
        sortOrder.value = "asc";
      }
      fetchLogs();
    };

    watch([searchQuery, startDate, endDate], () => {
      currentPage.value = 1;
      debouncedFetchLogs();
    });

    onMounted(fetchLogs);

    return {
      logs,
      searchQuery,
      startDate,
      endDate,
      currentPage,
      itemsPerPage,
      columns,
      isFiltered,
      totalPages,
      totalItems,
      error,
      isLoading,
      showLogDetails,
      changePage,
      changeItemsPerPage,
      formatDateTime,
      getStatusCodeClass,
      sortLogs,
      debouncedFetchLogs,
    };
  },
};
</script>
